import type { AxiosError } from 'axios';
import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';

import { ErrorCodes } from './ErrorCodes';
import type { IHttpClient } from './IHttpClient';
import { Result } from './Result';

export type HttpClientError = {
  status: number;
  message?: string;
  url?: string;
};

export class AxiosHttpClient implements IHttpClient {
  baseURL: string | undefined;

  baseParams: Record<string, string> | undefined;

  retries: number;

  client;

  constructor(
    props: {
      baseURL?: string;
      baseParams?: Record<string, string>;
      retries?: number;
      timeout?: number;
    } = {}
  ) {
    this.baseURL = props.baseURL;
    this.baseParams = props.baseParams;
    this.retries = props.retries || 1;

    this.client = axios.create({
      ...(this.baseURL && {
        baseURL: this.baseURL,
      }),
      timeout: props.timeout || 60 * 1000, // default 1 min
    });
    this.client.interceptors.request.use((config) => {
      config.headers['request-startTime'] = Date.now();
      return config;
    });
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    // Exponential back-off retry delay between requests
    axiosRetry(this.client, {
      retries: this.retries,
      retryDelay: axiosRetry.exponentialDelay,
      shouldResetTimeout: true,
      retryCondition: (error) => {
        return (
          isNetworkOrIdempotentRequestError(error) ||
          error.code === 'ECONNABORTED'
        );
      },
    });
  }

  public async Get<ResponseType>(
    url: string,
    headers?: any
  ): Promise<Result<ResponseType>> {
    try {
      const response = await this.client.get(url, {
        headers,
        params: this.baseParams,
      });
      if (response.status < 200 || response.status >= 400) {
        return Result.fail<any>({
          code: ErrorCodes.HTTP_CLIENT_ERROR,
          message: `AxiosHttpClient::GET: Received status code ${response.status} ${response.statusText}`,
        });
      }
      return Result.ok<any>(response.data);
    } catch (err: any) {
      return Result.fail<any>({
        code: ErrorCodes.HTTP_CLIENT_ERROR,
        message: err.message,
        httpError: {
          status: (err as AxiosError).response?.status as number,
          url: `${(err as AxiosError).response?.config.baseURL}${
            (err as AxiosError).response?.config.url
          }`,
        },
      });
    }
  }

  async Post<ResponseType>(
    url: string,
    body: any,
    headers?: any
  ): Promise<Result<ResponseType>> {
    try {
      const response = await this.client.post(url, body, {
        headers,
        params: this.baseParams,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });
      if (response.status < 200 || response.status >= 400) {
        return Result.fail<any>({
          code: ErrorCodes.HTTP_CLIENT_ERROR,
          message: `AxiosHttpClient::POST: Received status code ${response.status} ${response.statusText}`,
        });
      }
      return Result.ok<any>(response.data);
    } catch (err: any) {
      return Result.fail<any>({
        code: ErrorCodes.HTTP_CLIENT_ERROR,
        message: err.message,
        meta: err.response?.data, // meta data value is being used in curable service apis
        httpError: {
          status: (err as AxiosError).response?.status as number,
          url: `${(err as AxiosError).response?.config.baseURL}${
            (err as AxiosError).response?.config.url
          }`,
        },
      });
    }
  }

  async Put<ResponseType>(
    url: string,
    body: any,
    headers?: any
  ): Promise<Result<ResponseType>> {
    try {
      const response = await this.client.put(url, body, {
        headers,
        params: this.baseParams,
      });
      if (response.status < 200 || response.status >= 400) {
        return Result.fail<any>({
          code: ErrorCodes.HTTP_CLIENT_ERROR,
          message: `AxiosHttpClient::PUT: Received status code ${response.status} ${response.statusText}`,
        });
      }
      return Result.ok<any>(response.data);
    } catch (err: any) {
      return Result.fail<any>({
        code: ErrorCodes.HTTP_CLIENT_ERROR,
        message: err.message,
        httpError: {
          status: (err as AxiosError).response?.status as number,
          url: `${(err as AxiosError).response?.config.baseURL}${
            (err as AxiosError).response?.config.url
          }`,
        },
      });
    }
  }

  public async GetBuffer<ResponseType>(
    url: string,
    headers?: any
  ): Promise<Result<ResponseType>> {
    try {
      const response = await this.client.get(url, {
        headers,
        params: this.baseParams,
        responseType: 'arraybuffer',
      });
      if (response.status < 200 || response.status >= 400) {
        return Result.fail<any>({
          code: ErrorCodes.HTTP_CLIENT_ERROR,
          message: `AxiosHttpClient::GetBuffer: Received status code ${response.status} ${response.statusText}`,
        });
      }
      return Result.ok<any>(response.data);
    } catch (err: any) {
      return Result.fail<any>({
        code: ErrorCodes.HTTP_CLIENT_ERROR,
        message: err.message,
        httpError: {
          status: (err as AxiosError).response?.status as number,
          url: `${(err as AxiosError).response?.config.baseURL}${
            (err as AxiosError).response?.config.url
          }`,
        },
      });
    }
  }

  public async Delete<ResponseType>(
    url: string,
    headers?: any
  ): Promise<Result<ResponseType>> {
    try {
      const response = await this.client.delete(url, {
        headers,
        params: this.baseParams,
      });
      if (response.status < 200 || response.status >= 400) {
        return Result.fail<any>({
          code: ErrorCodes.HTTP_CLIENT_ERROR,
          message: `AxiosHttpClient::Delete: Received status code ${response.status} ${response.statusText}`,
        });
      }
      return Result.ok<any>(response.data);
    } catch (err: any) {
      return Result.fail<any>({
        code: ErrorCodes.HTTP_CLIENT_ERROR,
        message: err.message,
        httpError: {
          status: (err as AxiosError).response?.status as number,
          url: `${(err as AxiosError).response?.config.baseURL}${
            (err as AxiosError).response?.config.url
          }`,
        },
      });
    }
  }
}
